/*
 * Free gift auto-add
 * ------------------
 * Keeps the configured gift line in sync with its trigger product using only
 * the native Cart AJAX API (/cart.js, /cart/add.js, /cart/change.js). The
 * automatic discount still does the pricing — this just makes sure the line
 * exists so the discount has something to apply to.
 *
 * Config and the fetch/XHR interceptor come from snippets/free-gift.liquid.
 */
(function () {
  'use strict';

  var NS = window.BBFreeGift;
  if (!NS || !NS.config || !NS.config.rules || !NS.config.rules.length) return;

  var cfg = NS.config;
  var rules = cfg.rules;

  // Config arrives as flat (trigger -> gift) pairs, but several offers grant the
  // same gift — the beauty bag comes with four of them — and one offer grants
  // several. A discount's reach is a property of the *gift product*, not of any
  // one offer, so everything downstream works on targets: one per gift variant,
  // holding every rule that feeds it. Accounting per rule instead counts the
  // same coverage once per offer and charges the customer the difference.
  var targets = (function () {
    var byVariant = {};
    var order = [];
    rules.forEach(function (rule) {
      if (!rule.giftVariantId || !rule.triggerProductId) return;
      var key = String(rule.giftVariantId);
      if (!byVariant[key]) {
        byVariant[key] = {
          id: rule.giftHandle || key,
          variantId: rule.giftVariantId,
          handle: rule.giftHandle,
          title: rule.giftTitle,
          sources: []
        };
        order.push(key);
      }
      byVariant[key].sources.push(rule);
    });
    return order.map(function (k) { return byVariant[k]; });
  })();
  if (!targets.length) return;
  var GIFT_PROPERTY = cfg.property || '_free_gift';
  var ADDED_KEY = 'bb-free-gift:added';
  var DISMISSED_KEY = 'bb-free-gift:dismissed';
  var CAP_KEY = 'bb-free-gift:cap';

  // Never routed through the patched fetch, so our own writes can't re-trigger
  // the interceptor.
  var rawFetch = NS.rawFetch || window.fetch.bind(window);

  function log() {
    if (!cfg.debug) return;
    console.log.apply(console, ['[free-gift]'].concat(Array.prototype.slice.call(arguments)));
  }

  /* ── Cart API ─────────────────────────────────────────────────────────── */

  function request(url, options) {
    var opts = options || {};
    opts.credentials = 'same-origin';
    opts.headers = Object.assign(
      { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      opts.headers || {}
    );

    return rawFetch(url, opts).then(function (response) {
      return response.json().then(function (body) {
        if (!response.ok) {
          var error = new Error((body && body.description) || response.statusText);
          error.status = response.status;
          error.body = body;
          throw error;
        }
        return body;
      });
    });
  }

  function getCart() {
    return request(cfg.routes.cart);
  }

  // Shopify answers a non-integer quantity with 400 Bad Request, and a NaN that
  // slips through JSON.stringify becomes `null` — which is exactly how one bad
  // reading turns into an endless retry loop. Nothing reaches the network
  // without passing through here.
  function toQuantity(value) {
    var n = Math.floor(Number(value));
    return isFinite(n) && n >= 0 ? n : null;
  }

  function finiteOr(value, fallback) {
    var n = Number(value);
    return isFinite(n) ? n : fallback;
  }

  function postJSON(url, payload) {
    return request(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  /* ── Session memory ───────────────────────────────────────────────────── */

  // Flags are stored against the cart token they were set for. A new cart (the
  // customer checked out, or the cart expired) therefore starts clean instead
  // of inheriting a stale "already added" / "dismissed" state.
  function readMap(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      var parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function flagged(key, id, token) {
    return !!token && readMap(key)[id] === token;
  }

  function writeMap(key, map) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(map));
    } catch (e) {}
  }

  function setFlag(key, id, token) {
    var map = readMap(key);
    if (token) {
      map[id] = token;
    } else {
      delete map[id];
    }
    writeMap(key, map);
  }

  // How many gift units the discount actually covered last time we looked,
  // recorded with the trigger quantity it was measured at. Without this the
  // planner would keep re-adding the units the trim step just removed.
  function rememberCap(target, cart, quantity) {
    var map = readMap(CAP_KEY);
    map[target.id] = {
      token: cart.token,
      triggerQuantity: triggerQuantityFor(target, cart),
      cap: quantity
    };
    writeMap(CAP_KEY, map);
  }

  /* ── Loop protection ──────────────────────────────────────────────────── */

  // Every write exists only to reach a cart that needs no more writes. If a cart
  // never settles — Shopify reshuffling a discount as quantities change, the
  // drawer undoing us, a request that keeps failing — we stop instead of
  // fighting it. A gift that quietly fails to appear is a support ticket; a cart
  // that mutates in a loop is a broken checkout.
  var HALTED_KEY = 'bb-free-gift:halted';
  var MAX_UNSETTLED_ROUNDS = 6;
  var MAX_CONSECUTIVE_FAILURES = 3;

  var unsettledRounds = 0;
  var consecutiveFailures = 0;

  function halted(token) {
    return !!token && readMap(HALTED_KEY)[token] === true;
  }

  function halt(token, reason) {
    log('HALTED —', reason + '. No further cart writes for this cart.');
    unsettledRounds = 0;
    consecutiveFailures = 0;
    if (!token) return;
    var map = {};
    map[token] = true;
    writeMap(HALTED_KEY, map);
  }

  function noteFailure(token) {
    consecutiveFailures++;
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      halt(token, consecutiveFailures + ' consecutive cart API failures');
    }
  }

  function noteSettled() {
    unsettledRounds = 0;
    consecutiveFailures = 0;
  }

  function noteUnsettled(token) {
    unsettledRounds++;
    if (unsettledRounds >= MAX_UNSETTLED_ROUNDS) {
      halt(token, 'cart did not settle after ' + unsettledRounds + ' write rounds');
    }
  }

  function capFor(target, token, triggerQuantity) {
    var entry = readMap(CAP_KEY)[target.id];
    if (!entry || entry.token !== token || !isFinite(entry.cap)) return null;
    // More triggers than when we measured — the discount may stretch further,
    // so let it probe again rather than under-gifting.
    if (triggerQuantity > entry.triggerQuantity) return null;
    return entry.cap;
  }

  /* ── Planning ─────────────────────────────────────────────────────────── */

  // Ours iff it is the gift variant AND carries our property. Deliberately not
  // "any zero-priced line of the gift variant": the discount matches on variant,
  // so it spills onto a unit the customer paid for, and treating that as ours
  // would let the quantity lock shrink a line they actually bought.
  function isGiftLine(item, target) {
    return !!(
      item.variant_id === target.variantId &&
      item.properties &&
      item.properties[GIFT_PROPERTY]
    );
  }

  function giftLinesFor(cart, target) {
    return (cart.items || []).filter(function (item) { return isGiftLine(item, target); });
  }

  function findGiftLine(cart, target) {
    return giftLinesFor(cart, target)[0] || null;
  }

  function quantityOfProduct(productId, cart) {
    var total = 0;
    (cart.items || []).forEach(function (item) {
      if (item.product_id === productId) total += item.quantity;
    });
    return total;
  }

  function triggerQuantityFor(target, cart) {
    var total = 0;
    target.sources.forEach(function (source) {
      total += quantityOfProduct(source.triggerProductId, cart);
    });
    return total;
  }

  // Units of the gift product this script did not add — the customer's own, or
  // another gift mechanism's (KwikCart's offers module, another app).
  function unitsNotOurs(cart, target) {
    var total = 0;
    (cart.items || []).forEach(function (item) {
      if (item.variant_id !== target.variantId) return;
      if (isGiftLine(item, target)) return;
      total += item.quantity;
    });
    return total;
  }

  function isAvailable(target) {
    return target.sources.some(function (s) { return !!s.giftAvailable; });
  }

  // Our gift for a product can legitimately occupy more than one line: when two
  // offers both grant it, Shopify splits the line and allocates a different
  // discount to each half — that split is exactly what makes both discounts
  // apply. So these lines are treated as one pool and only their *total* is
  // steered. Deleting the "extra" line would throw away the second discount.
  function resizeTo(giftLines, target, token, desired) {
    var actions = [];
    var remaining = desired;
    giftLines.forEach(function (line) {
      var keep = Math.max(0, Math.min(line.quantity, remaining));
      remaining -= keep;
      if (keep !== line.quantity) {
        actions.push({ type: 'change', key: line.key, quantity: keep, target: target, token: token });
      }
    });
    return actions;
  }

  // Actions needed to bring `target` in line with `cart` — usually none or one,
  // but always an array so duplicate lines can be collapsed too.
  function planFor(target, cart) {
    var token = cart.token;
    var giftLines = giftLinesFor(cart, target);
    var giftLine = giftLines[0] || null;
    var giftQuantity = giftLines.reduce(function (sum, item) { return sum + item.quantity; }, 0);
    var actions = [];


    // Every offer granting this product contributes. A cart holding both The
    // Morning Edit and The Evening Edit earns a beauty bag from each.
    var triggerQuantity = 0;
    var wanted = 0;

    target.sources.forEach(function (source) {
      var present = quantityOfProduct(source.triggerProductId, cart);
      if (present <= 0) return;
      triggerQuantity += present;
      var earned = source.multiply ? present * source.giftQuantity : source.giftQuantity;
      if (source.maxQuantity && earned > source.maxQuantity) earned = source.maxQuantity;
      wanted += earned;
    });

    if (triggerQuantity === 0) {
      // No offer active — drop the gift and reset the memory so it all works
      // again if the customer re-adds a trigger.
      setFlag(ADDED_KEY, target.id, null);
      setFlag(DISMISSED_KEY, target.id, null);
      setFlag(CAP_KEY, target.id, null);
      if (giftLine) actions = actions.concat(resizeTo(giftLines, target, token, 0));
      return actions;
    }

    // Never hand out more than the discount will zero-price, or the overflow
    // lands in the cart as a paid line. `maxQuantity` is the merchant's own
    // ceiling; the cap is what Shopify's discount was measured to cover.
    var cap = capFor(target, token, triggerQuantity);
    if (cap !== null && wanted > cap) wanted = cap;

    // Units of this product already in the cart that we did not put there —
    // the customer's, KwikCart's own gift module, any other app. We stand down
    // for each of them. Without this, two mechanisms granting the same gift each
    // add a line and the shopper sees the product twice, once free and once
    // charged, which is exactly what a discount covering one unit looks like.
    if (cfg.deferToExistingUnits !== false) {
      var foreign = unitsNotOurs(cart, target);
      if (foreign > 0) {
        wanted = Math.max(0, wanted - foreign);
        log('standing down for', foreign, 'existing unit(s) of', target.id, '→ want', wanted);
      }
    }

    if (wanted === 0) {
      // Nothing to grant. The flags are deliberately not reset here: that only
      // happens when the triggers leave, otherwise we'd re-add in a loop.
      if (giftLine) actions = actions.concat(resizeTo(giftLines, target, token, 0));
      return actions;
    }

    if (!giftLine) {
      // Gift missing but we already placed it in this cart → the customer took
      // it out. Re-adding would fight them, so remember the choice instead.
      if (cfg.respectManualRemoval !== false && flagged(ADDED_KEY, target.id, token)) {
        setFlag(ADDED_KEY, target.id, null);
        setFlag(DISMISSED_KEY, target.id, token);
        log('gift removed by customer, not re-adding:', target.id);
        return actions;
      }
      if (cfg.respectManualRemoval !== false && flagged(DISMISSED_KEY, target.id, token)) return actions;
      if (!isAvailable(target)) {
        log('gift variant unavailable, skipping:', target.handle);
        return actions;
      }
      actions.push({ type: 'add', target: target, quantity: wanted, token: token });
      return actions;
    }

    if (giftQuantity !== wanted && cfg.lockGiftQuantity !== false) {
      if (wanted > giftQuantity) {
        // Top up. Shopify merges the addition into an existing line, or splits
        // it again if a second discount can cover the extra units.
        actions.push({ type: 'add', target: target, quantity: wanted - giftQuantity, token: token });
      } else {
        actions = actions.concat(resizeTo(giftLines, target, token, wanted));
      }
    }

    return actions;
  }

  /* ── Applying ─────────────────────────────────────────────────────────── */

  function applyActions(actions) {
    var chain = Promise.resolve();

    var changes = actions.filter(function (action) { return action.type === 'change'; });

    if (changes.length) {
      // Re-read the cart immediately before writing. A key we planned against
      // can go stale if KwikCart moved the cart underneath us, and a stale key
      // must never be allowed to resolve to a line the customer owns. Every
      // write is gated on the line still carrying our own property, so this
      // script structurally cannot modify anything it did not create.
      chain = chain.then(getCart).then(function (fresh) {
        var byKey = {};
        (fresh.items || []).forEach(function (item) { byKey[item.key] = item; });

        var writes = Promise.resolve();

        changes.forEach(function (action) {
          writes = writes.then(function () {
            if (halted(action.token)) return null;

            var quantity = toQuantity(action.quantity);
            if (quantity === null) {
              log('skipped change — invalid quantity', action.quantity, 'for', action.key);
              return null;
            }

            var line = byKey[action.key];
            if (!line || !isGiftLine(line, action.target)) {
              log('skipped change — line is not ours:', action.key);
              return null;
            }
            if (line.quantity === quantity) return null;

            return postJSON(cfg.routes.change, { id: action.key, quantity: quantity })
              .then(function () {
                consecutiveFailures = 0;
                if (quantity === 0) setFlag(ADDED_KEY, action.target.id, null);
              })
              .catch(function (error) {
                log('failed to update gift line', action.target.id, error);
                noteFailure(action.token);
              });
          });
        });

        return writes;
      });
    }

    var adds = actions.filter(function (action) { return action.type === 'add'; });
    if (adds.length) {
      chain = chain.then(function () {
        var usable = adds.filter(function (action) {
          if (halted(action.token)) return false;
          var quantity = toQuantity(action.quantity);
          if (quantity === null || quantity === 0) {
            log('skipped add — invalid quantity', action.quantity);
            return false;
          }
          action.quantity = quantity;
          return true;
        });

        if (!usable.length) return null;

        var items = usable.map(function (action) {
          var properties = {};
          properties[GIFT_PROPERTY] = action.target.id;
          return { id: action.target.variantId, quantity: action.quantity, properties: properties };
        });

        return postJSON(cfg.routes.add, { items: items })
          .then(function () {
            consecutiveFailures = 0;
            usable.forEach(function (action) { setFlag(ADDED_KEY, action.target.id, action.token); });
            log('added gift(s)', items);
          })
          .catch(function (error) {
            // Out of stock, discount removed, etc. — leave the cart as the
            // customer built it rather than blocking their add-to-cart.
            log('failed to add gift', error);
            noteFailure(usable[0].token);
          });
      });
    }

    return chain;
  }

  /* ── Discount verification ────────────────────────────────────────────── */

  // How much of the *free-gift* discount landed on a line, by name. Counting
  // only the named discount is what keeps an unrelated sitewide promo on the
  // Titles of the freebie discounts, if configured. Accepts a list (preferred)
  // or a single string for backwards compatibility.
  var DISCOUNT_TITLES = (function () {
    var t = cfg.discountTitles || cfg.discountTitle;
    if (!t) return [];
    if (typeof t === 'string') t = [t];
    return t.filter(Boolean).map(function (x) { return String(x).toLowerCase(); });
  })();

  // How much of a *gift* discount landed on this line. Counting only the named
  // discounts — or, with none configured, only allocations that take 100% off —
  // is what keeps an unrelated sitewide promo on the same product from being
  // mistaken for gift coverage and leaving the shopper paying for a "free" unit.
  function giftAllocationOn(item) {
    var sum = 0;

    (item.line_level_discount_allocations || []).forEach(function (allocation) {
      var application = allocation.discount_application || {};
      // A missing or non-numeric amount must contribute nothing. Letting one
      // through makes the sum NaN, and NaN fails every comparison below, so the
      // trim would fire on every pass forever.
      var amount = finiteOr(allocation.amount, 0);
      if (!amount) return;

      if (DISCOUNT_TITLES.length) {
        if (DISCOUNT_TITLES.indexOf(String(application.title || '').toLowerCase()) > -1) sum += amount;
        return;
      }

      if (String(application.value_type) === 'percentage' && finiteOr(application.value, 0) >= 100) {
        sum += amount;
      }
    });

    return sum;
  }

  // Units of the gift variant the discount is covering, across *every* line it
  // sits on — including one the customer added themselves.
  //
  // Cart-wide on purpose. Shopify decides which line to allocate a "buy X get Y"
  // discount to and that choice is arbitrary; the cart total is identical either
  // way. Asking "is *my* line free?" instead is what made a customer's own unit
  // appear to vanish — the discount landed on their line, ours looked uncovered,
  // and we deleted the gift.
  function coveredUnitsFor(cart, target) {
    var unit = 0;
    var allocated = 0;
    var wholeFreeUnits = 0;

    (cart.items || []).forEach(function (item) {
      if (item.variant_id !== target.variantId) return;
      if (!unit) unit = finiteOr(item.original_price, 0);
      allocated += giftAllocationOn(item);
      if (item.final_line_price === 0) wholeFreeUnits += item.quantity;
    });

    if (!unit) return 0;

    // Both are lower bounds on true coverage — a line costing nothing is
    // certainly free, and so is anything a gift discount paid for. Taking the
    // larger keeps the estimate tight while staying a lower bound, so the worst
    // case is under-gifting, never an unexpected charge.
    var byAllocation = finiteOr(Math.floor(allocated / unit), 0);
    return Math.max(byAllocation, finiteOr(wholeFreeUnits, 0));
  }

  // Only Shopify knows how far the discount stretches — a "buy X get Y" use
  // limit is not readable from the storefront — so we add first, then hand back
  // any unit it did not cover rather than leaving it in the cart as a charge.
  function trimUncoveredGifts(cart) {
    var current = cart;
    var trimmed = false;

    var actions = [];

    targets.forEach(function (target) {
      var lines = giftLinesFor(current, target);
      if (!lines.length) return;

      // Total across every line of ours for this product — Shopify may have
      // split them so two discounts can each cover a share.
      var held = lines.reduce(function (sum, l) { return sum + l.quantity; }, 0);
      var covered = coveredUnitsFor(current, target);
      rememberCap(target, current, covered);

      if (held <= covered) return;

      log('gift exceeds discount coverage:', target.id, held, '→', covered);
      actions = actions.concat(resizeTo(lines, target, current.token, covered));
    });

    if (!actions.length) return Promise.resolve({ cart: current, trimmed: false });

    trimmed = true;

    return applyActions(actions)
      .then(getCart)
      .then(function (updated) {
        return { cart: updated, trimmed: trimmed };
      });
  }

  /* ── UI refresh ───────────────────────────────────────────────────────── */

  var CART_EVENTS = ['cart:updated', 'cart:refresh', 'cart:change', 'cartUpdated', 'ajaxCart:afterCartLoad'];

  function broadcast(cart) {
    CART_EVENTS.forEach(function (name) {
      var detail = { cart: cart, source: 'bb-free-gift' };
      try {
        document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
        window.dispatchEvent(new CustomEvent(name, { detail: detail }));
        if (document.body) {
          document.body.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
        }
      } catch (e) {}
    });

    try {
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'bb-free-gift', cartData: cart });
      }
    } catch (e) {}
  }

  // Sections the theme itself re-renders after a cart change. KwikCart owns the
  // drawer, but the header/mobile bubbles and the /cart page are still ours.
  function themeSections() {
    var sections = [];

    function addStatic(id) {
      var element = document.getElementById(id);
      if (element) sections.push({ section: id, target: element, selector: '.shopify-section' });
    }

    addStatic('cart-icon-bubble');
    addStatic('mobile-cart-icon-bubble');
    addStatic('mini-cart');

    // The header mini-cart reuses #main-cart-items without a data-id, so match
    // on the attribute to be sure we get the /cart page's section.
    ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
      var element = document.querySelector('#' + id + '[data-id]');
      if (element) sections.push({ section: element.dataset.id, target: element, selector: '.js-contents' });
    });

    return sections;
  }

  function refreshSections() {
    var sections = themeSections();
    if (!sections.length) return Promise.resolve();

    var names = sections
      .map(function (entry) { return entry.section; })
      .filter(function (name, index, all) { return all.indexOf(name) === index; });

    var url = (cfg.routes.root || '/') + '?sections=' + encodeURIComponent(names.join(','));

    return request(url)
      .then(function (rendered) {
        sections.forEach(function (entry) {
          var html = rendered[entry.section];
          if (!html || !entry.target.isConnected) return;
          try {
            var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(entry.selector);
            if (parsed) entry.target.innerHTML = parsed.innerHTML;
          } catch (e) {
            log('section render failed', entry.section, e);
          }
        });
      })
      .catch(function (error) {
        log('section refresh failed', error);
      });
  }

  // The request that triggered us only resolves *after* we finish, and its
  // caller then paints section HTML that Shopify rendered before the gift
  // existed. Repaint once those late writes have landed.
  var lateTimers = [];

  function lateRefresh(cart) {
    lateTimers.forEach(clearTimeout);
    lateTimers = [400, 1200].map(function (delay) {
      return setTimeout(function () {
        broadcast(cart);
        refreshSections();
      }, delay);
    });

    // Another gift mechanism — KwikCart's own offers module, or any app — may
    // add the same product a moment after we do, which would leave the shopper
    // looking at it twice. Re-check once the dust settles so the deferral above
    // can stand our line down. On an unchanged cart this is a single /cart.js
    // read that writes nothing, so it cannot keep the loop alive itself.
    lateTimers.push(setTimeout(schedule, 1800));
  }

  /* ── Reconcile loop ───────────────────────────────────────────────────── */

  var queue = Promise.resolve();
  var running = false;

  function reconcile() {
    if (running) return Promise.resolve();
    running = true;

    return getCart()
      .then(function (cart) {
        if (halted(cart.token)) return null;

        var actions = [];
        targets.forEach(function (target) {
          actions = actions.concat(planFor(target, cart));
        });

        var settled = actions.length
          ? applyActions(actions).then(getCart)
          : Promise.resolve(cart);

        if (actions.length) log('applying', actions);

        // Always verify, even when the plan was a no-op: a cart can arrive
        // already over-gifted (a stale session, or a discount the merchant has
        // since capped) and needs trimming back.
        return settled.then(trimUncoveredGifts).then(function (result) {
          var wrote = actions.length || result.trimmed;

          // A round that changed nothing is the definition of settled. A run of
          // rounds that keep writing is the definition of a loop, and trips the
          // breaker before the customer ever sees the cart flicker.
          if (!wrote) {
            noteSettled();
            return null;
          }
          noteUnsettled(result.cart && result.cart.token);

          broadcast(result.cart);
          lateRefresh(result.cart);
          return refreshSections();
        });
      })
      .catch(function (error) {
        log('reconcile failed', error);
      })
      .then(function () {
        running = false;
      });
  }

  // Serialised so overlapping cart mutations can't race each other into
  // duplicate gift lines.
  function schedule() {
    queue = queue.then(reconcile, reconcile);
    return queue;
  }

  NS.handler = schedule;
  NS.reconcile = schedule;

  if (NS.pending) {
    NS.pending = false;
    schedule();
  }

  // KwikCart and other apps can change the cart without a /cart/* request the
  // interceptor can see, but they all announce it. Our own broadcasts carry a
  // source tag so they don't feed back into the loop, and the debounce keeps a
  // chatty app from turning every render into a /cart.js round trip.
  var eventTimer = null;

  CART_EVENTS.forEach(function (name) {
    document.addEventListener(name, function (event) {
      if (event && event.detail && event.detail.source === 'bb-free-gift') return;
      clearTimeout(eventTimer);
      eventTimer = setTimeout(schedule, 150);
    });
  });

  // Catches carts that already contain the trigger — restored sessions, a
  // /cart page load, or a checkout bounce-back.
  function boot() {
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) schedule();
  });
})();
